import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Scatter, ScatterChart, ZAxis,
} from 'recharts';
import type { OBVisitSummary } from '../types/pregnancy';

interface Props {
  visits: OBVisitSummary[];
}

// SA Maternity Case Record reference curves (Jacobus/Studd centile approach)
// Lower (10th centile): sfh = week - 4, from week 20
// Upper (90th centile): sfh = week + 4, from week 20
const referenceData = Array.from({ length: 29 }, (_, i) => ({
  week: i + 12,
  lower: i + 12 >= 20 ? (i + 12) - 4 : null,
  upper: i + 12 >= 20 ? (i + 12) + 4 : null,
}));

const TEAL = '#0D7377';
const RED = '#DC2626';

interface ChartDot {
  week: number;
  sfh: number;
  date: string;
  gestationWeeks: number;
  gestationDays: number | null;
  inRange: boolean;
}

/**
 * SFH growth chart — plots symphysis-fundal height against gestational age
 * with SA reference curves (10th and 90th centile bands).
 *
 * Self-contained: no API calls. Receives visits as a prop.
 */
export function SFHChart({ visits }: Props) {
  const dots = useMemo<ChartDot[]>(() =>
    visits
      .filter(v => v.sfh != null && v.gestationWeeks != null)
      .map(v => {
        const week = v.gestationWeeks! + (v.gestationDays ?? 0) / 7;
        const sfh = v.sfh!;
        const lower = week >= 20 ? week - 4 : null;
        const upper = week >= 20 ? week + 4 : null;
        const inRange = lower != null && upper != null ? sfh >= lower && sfh <= upper : true;
        return {
          week: Math.round(week * 10) / 10,
          sfh,
          date: v.date,
          gestationWeeks: v.gestationWeeks!,
          gestationDays: v.gestationDays,
          inRange,
        };
      }),
  [visits]);

  if (dots.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
        <p className="text-sm">No SFH measurements recorded yet.</p>
        <p className="text-xs mt-1">SFH data points will appear on the chart as obstetric consultations are logged.</p>
      </div>
    );
  }

  // Combine reference lines + dots into a single dataset for the chart
  const chartData = referenceData.map(r => {
    const dot = dots.find(d => Math.abs(d.week - r.week) < 0.5);
    return {
      ...r,
      sfhDot: dot?.sfh ?? null,
      dotColor: dot?.inRange ? TEAL : RED,
      dotDate: dot?.date,
      dotGestation: dot ? `${dot.gestationWeeks}w${dot.gestationDays ? ` ${dot.gestationDays}d` : ''}` : null,
    };
  });

  // Add dots that don't align with whole weeks
  for (const dot of dots) {
    const existing = chartData.find(c => Math.abs(c.week - dot.week) < 0.5);
    if (!existing) {
      chartData.push({
        week: dot.week,
        lower: dot.week >= 20 ? dot.week - 4 : null,
        upper: dot.week >= 20 ? dot.week + 4 : null,
        sfhDot: dot.sfh,
        dotColor: dot.inRange ? TEAL : RED,
        dotDate: dot.date,
        dotGestation: `${dot.gestationWeeks}w${dot.gestationDays ? ` ${dot.gestationDays}d` : ''}`,
      });
    }
  }
  chartData.sort((a, b) => a.week - b.week);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">SFH Growth Chart</h3>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="week"
            type="number"
            domain={[12, 40]}
            ticks={[12, 16, 20, 24, 28, 32, 36, 40]}
            label={{ value: 'Gestational Age (weeks)', position: 'insideBottom', offset: -5, fontSize: 11 }}
            fontSize={11}
          />
          <YAxis
            domain={[0, 45]}
            ticks={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45]}
            label={{ value: 'SFH (cm)', angle: -90, position: 'insideLeft', offset: 5, fontSize: 11 }}
            fontSize={11}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0]?.payload;
              if (!p?.sfhDot) return null;
              return (
                <div className="bg-white border border-gray-200 rounded shadow-lg p-2 text-xs">
                  <p className="font-semibold">{p.dotDate}</p>
                  <p>Gestation: {p.dotGestation}</p>
                  <p>SFH: {p.sfhDot}cm</p>
                  <p className={p.dotColor === TEAL ? 'text-teal-700' : 'text-red-700'}>
                    {p.dotColor === TEAL ? 'Within reference range' : 'Outside reference range'}
                  </p>
                </div>
              );
            }}
          />
          {/* Reference curves — dashed */}
          <Line type="monotone" dataKey="lower" stroke="#9CA3AF" strokeDasharray="5 5" dot={false} name="10th centile" connectNulls={false} />
          <Line type="monotone" dataKey="upper" stroke="#9CA3AF" strokeDasharray="5 5" dot={false} name="90th centile" connectNulls={false} />
          {/* SFH dots */}
          <Line
            type="monotone"
            dataKey="sfhDot"
            stroke="none"
            dot={(props: Record<string, unknown>) => {
              const { cx, cy, payload } = props as { cx: number; cy: number; payload: { dotColor: string; sfhDot: number | null } };
              if (!payload?.sfhDot) return <></>;
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={5}
                  fill={payload.dotColor}
                  stroke="white"
                  strokeWidth={2}
                />
              );
            }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 border-t-2 border-dashed border-gray-400" /> Reference band (10th–90th centile)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TEAL }} /> In range
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: RED }} /> Out of range
        </span>
      </div>
    </div>
  );
}
